import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Building2,
  User,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ChevronDown,
  Calendar,
  FileText,
  FolderOpen,
  Receipt,
  Shield,
  Ticket,
  MessageSquare,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';

type NavItem = {
  labelKey: string;
  href: string;
  icon: typeof Home;
};

const PRIMARY_NAV: NavItem[] = [
  { labelKey: 'portal.nav.dashboard', href: '/portal/dashboard', icon: Home },
  { labelKey: 'portal.nav.messages', href: '/portal/messages', icon: MessageSquare },
  { labelKey: 'portal.nav.appointments', href: '/portal/appointments', icon: Calendar },
  { labelKey: 'portal.nav.documents', href: '/portal/documents', icon: FolderOpen },
  { labelKey: 'portal.nav.invoices', href: '/portal/invoices', icon: Receipt },
  { labelKey: 'portal.nav.punch_cards', href: '/portal/punch-cards', icon: Ticket },
  { labelKey: 'portal.nav.questionnaires', href: '/portal/questionnaires', icon: FileText },
];

const ACCOUNT_NAV: NavItem[] = [
  { labelKey: 'portal.nav.profile', href: '/portal/profile', icon: User },
  { labelKey: 'portal.nav.clinics', href: '/portal/clinics', icon: Building2 },
  { labelKey: 'portal.nav.data_export', href: '/portal/data-export', icon: Shield },
];

function NavLink({
  item,
  isActive,
  onClick,
  compact = false,
}: {
  item: NavItem;
  isActive: boolean;
  onClick?: () => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const label = t(item.labelKey);

  return (
    <Link
      to={item.href}
      onClick={onClick}
      title={compact ? label : undefined}
      className={`flex items-center rounded-lg text-sm font-medium transition-colors ${
        compact ? 'px-2.5 py-2' : 'px-3 py-2'
      } ${
        isActive
          ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
          : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50'
      }`}
    >
      <Icon className={`h-4 w-4 ${compact ? '' : 'mr-2'}`} />
      <span className={compact ? 'sr-only xl:not-sr-only xl:ml-2' : ''}>{label}</span>
    </Link>
  );
}

/**
 * Navigation header for the patient portal.
 * Primary items stay in the top bar; account items live in the profile menu.
 */
export function PortalNavigation() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { clinics, activeClinicId, setActiveClinicId } = usePortalActiveClinic();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showClinicDropdown, setShowClinicDropdown] = useState(false);

  const activeClinic = clinics.find((c) => c.clinic_id === activeClinicId);
  const allNav = [...PRIMARY_NAV, ...ACCOUNT_NAV];

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/portal/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/portal/login');
    }
  };

  const isActivePath = (path: string) => location.pathname.startsWith(path);

  return (
    <>
      <nav className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2 lg:gap-3">
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="flex shrink-0 items-center justify-center rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 lg:hidden"
                aria-label="Toggle menu"
              >
                {showMobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>

              <Link to="/portal/dashboard" className="shrink-0">
                <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400 sm:text-2xl">
                  Clinio
                </span>
              </Link>

              {clinics.length > 0 && (
                <div className="relative hidden shrink-0 lg:block">
                  <button
                    type="button"
                    onClick={() => setShowClinicDropdown(!showClinicDropdown)}
                    className="flex max-w-[180px] items-center rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 xl:max-w-[220px]"
                  >
                    <Building2 className="mr-1.5 h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {activeClinic?.clinic_name ?? t('portal.clinics.select')}
                    </span>
                    <ChevronDown className="ml-1 h-4 w-4 shrink-0" />
                  </button>
                  {showClinicDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowClinicDropdown(false)} />
                      <div className="absolute left-0 z-20 mt-2 w-64 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                        {clinics.map((clinic) => (
                          <button
                            key={clinic.clinic_id}
                            type="button"
                            onClick={() => {
                              setActiveClinicId(clinic.clinic_id);
                              setShowClinicDropdown(false);
                            }}
                            className={`flex w-full items-center px-4 py-2 text-left text-sm ${
                              clinic.clinic_id === activeClinicId
                                ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                                : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            {clinic.clinic_name}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="hidden min-w-0 flex-1 items-center justify-end gap-0.5 overflow-hidden lg:flex xl:gap-1">
                {PRIMARY_NAV.map((item) => (
                  <NavLink
                    key={item.href}
                    item={item}
                    isActive={isActivePath(item.href)}
                    compact
                  />
                ))}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1 sm:gap-2">
              <button
                onClick={toggleTheme}
                className="flex items-center justify-center rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                title={theme === 'light' ? t('portal.nav.dark_mode') : t('portal.nav.light_mode')}
              >
                {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              </button>

              <div className="relative hidden lg:block">
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className={`flex max-w-[140px] items-center rounded-lg px-2.5 py-2 text-sm font-medium transition-colors xl:max-w-none xl:px-3 ${
                    showProfileDropdown || ACCOUNT_NAV.some((item) => isActivePath(item.href))
                      ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  <User className="mr-1.5 h-4 w-4 shrink-0" />
                  <span className="truncate">{user?.email?.split('@')[0] || t('portal.nav.account')}</span>
                  <ChevronDown className="ml-1 h-4 w-4 shrink-0" />
                </button>

                {showProfileDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowProfileDropdown(false)} />
                    <div className="absolute right-0 z-20 mt-2 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                      <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {t('portal.nav.signed_in_as')}
                        </div>
                        <div className="mt-1 truncate text-sm font-medium text-gray-900 dark:text-white">
                          {user?.email}
                        </div>
                      </div>

                      {ACCOUNT_NAV.map((item) => {
                        const Icon = item.icon;
                        const isActive = isActivePath(item.href);
                        return (
                          <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => setShowProfileDropdown(false)}
                            className={`flex items-center px-4 py-2 text-sm transition-colors ${
                              isActive
                                ? 'bg-gray-50 text-gray-900 dark:bg-gray-700/50 dark:text-white'
                                : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            <Icon className="mr-3 h-4 w-4" />
                            {t(item.labelKey)}
                          </Link>
                        );
                      })}

                      <button
                        onClick={() => {
                          setShowProfileDropdown(false);
                          void handleLogout();
                        }}
                        className="flex w-full items-center border-t border-gray-200 px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-50 dark:border-gray-700 dark:text-red-400 dark:hover:bg-red-900/20"
                      >
                        <LogOut className="mr-3 h-4 w-4" />
                        {t('portal.nav.logout')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {showMobileMenu && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
            onClick={() => setShowMobileMenu(false)}
          />

          <div className="fixed bottom-0 left-0 top-0 z-50 w-80 max-w-[85vw] overflow-y-auto bg-white shadow-xl dark:bg-gray-800 lg:hidden">
            <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
              <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">Clinio</span>
              <button
                onClick={() => setShowMobileMenu(false)}
                className="rounded-lg p-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-2 p-4">
              {clinics.length > 0 && (
                <div className="border-b border-gray-200 pb-2 dark:border-gray-700">
                  <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {t('portal.clinics.select')}
                  </div>
                  {clinics.map((clinic) => (
                    <button
                      key={clinic.clinic_id}
                      type="button"
                      onClick={() => {
                        setActiveClinicId(clinic.clinic_id);
                        setShowMobileMenu(false);
                      }}
                      className={`flex w-full items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                        clinic.clinic_id === activeClinicId
                          ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                          : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <Building2 className="mr-3 h-5 w-5" />
                      {clinic.clinic_name}
                    </button>
                  ))}
                </div>
              )}

              <div className="border-b border-gray-200 pb-2 dark:border-gray-700">
                <button
                  onClick={toggleTheme}
                  className="flex w-full items-center rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {theme === 'light' ? (
                    <>
                      <Moon className="mr-3 h-5 w-5" />
                      {t('portal.nav.dark_mode')}
                    </>
                  ) : (
                    <>
                      <Sun className="mr-3 h-5 w-5" />
                      {t('portal.nav.light_mode')}
                    </>
                  )}
                </button>
              </div>

              <div className="space-y-1">
                {allNav.map((item) => (
                  <NavLink
                    key={item.href}
                    item={item}
                    isActive={isActivePath(item.href)}
                    onClick={() => setShowMobileMenu(false)}
                  />
                ))}
              </div>

              <div className="space-y-1 border-t border-gray-200 pt-2 dark:border-gray-700">
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {t('portal.nav.account')}
                </div>
                <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{user?.email}</div>
                <button
                  onClick={() => {
                    setShowMobileMenu(false);
                    void handleLogout();
                  }}
                  className="flex w-full items-center rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                >
                  <LogOut className="mr-3 h-5 w-5" />
                  {t('portal.nav.logout')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
